import { AddressDisplay, cn, EmptyState, Skeleton, Table } from "@gotts.ai/ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import { decodeFunctionResult, encodeFunctionData } from "viem";

import { CollapsibleSection } from "../../components/CollapsibleSection";
import { PcbCard } from "../../components/PcbCard";
import { ProtocolBadge } from "../../components/ProtocolBadge";
import { TokenIcon } from "../../components/TokenIcon";
import { useDeployment } from "../../context";
import { useBlockNumber } from "../../hooks/useBlockNumber";
import { useRpcRequest } from "../../hooks/useRpcRequest";
import {
  STATE_VIEW_ABI,
  V2_PAIR_ABI,
  V3_FACTORY_ABI,
  V3_POOL_ABI,
} from "../../lib/abis";
import { DEVENV_TOKEN_PRICES } from "../../lib/constants";
import { formatUsd } from "../../lib/format";

import {
  computePairAddress,
  computePoolId,
  computeTvlUsd,
  getDecimals,
  V2_POOLS,
  V3_POOLS,
  V4_POOLS,
} from "./types";
import { V2PoolCard } from "./V2PoolCard";
import { V3PoolCard } from "./V3PoolCard";
import { V4PoolCard } from "./V4PoolCard";

import type { V2PoolState, V3PoolState, V4PoolState } from "./types";
import type { Address } from "viem";

type SortMode = "default" | "pair-az" | "fee-low";

/** Protocol tab config with colors */
const PROTOCOL_TABS = [
  { value: "V2", color: "#818cf8" },
  { value: "V3", color: "#60a5fa" },
  { value: "V4", color: "#2dd4bf" },
] as const;

const tokenColumns = [
  {
    key: "symbol",
    label: "Symbol",
    render: (_v: unknown, row: Record<string, unknown>) => (
      <span className="flex items-center gap-1.5">
        <TokenIcon symbol={row.symbol as string} size={16} />
        <span>{row.symbol as string}</span>
      </span>
    ),
  },
  { key: "decimals", label: "Decimals" },
  {
    key: "address",
    label: "Address",
    render: (_v: unknown, row: Record<string, unknown>) => (
      <AddressDisplay address={row.address as string} fontSize={10} copyable />
    ),
  },
];

export default function PoolExplorer() {
  const { deployment, loading } = useDeployment();
  const { data: blockNumber } = useBlockNumber();
  const { request } = useRpcRequest();

  const [v2States, setV2States] = useState<Record<string, V2PoolState>>({});
  const [v3States, setV3States] = useState<Record<string, V3PoolState>>({});
  const [v4States, setV4States] = useState<Record<string, V4PoolState>>({});
  const [activeTab, setActiveTab] = useState<string>("V2");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortMode, setSortMode] = useState<SortMode>("default");

  function getTokenAddress(symbol: string): Address | null {
    if (!deployment?.tokens) return null;
    const tokens = deployment.tokens as unknown as Record<string, string>;
    const addr = tokens[symbol];
    return addr ? (addr as Address) : null;
  }

  function getTokenPair(tokenKeys: readonly string[]): [string, string] | null {
    const a = getTokenAddress(tokenKeys[0] ?? "");
    const b = getTokenAddress(tokenKeys[1] ?? "");
    if (a && b) return [a, b];
    return null;
  }

  function sortAddresses(a: Address, b: Address): [Address, Address] {
    return a.toLowerCase() < b.toLowerCase() ? [a, b] : [b, a];
  }

  const readContract = useCallback(
    async (
      address: Address,
      abi: readonly unknown[],
      functionName: string,
      args: unknown[] = [],
    ) => {
      const data = encodeFunctionData({
        abi: abi as Parameters<typeof encodeFunctionData>[0]["abi"],
        functionName,
        args,
      });
      const result = await request("eth_call", [
        { to: address, data },
        "latest",
      ]);
      if (!result || result === "0x") return null;
      return decodeFunctionResult({
        abi: abi as Parameters<typeof decodeFunctionResult>[0]["abi"],
        functionName,
        data: result as `0x${string}`,
      });
    },
    [request],
  );

  // -----------------------------------------------------------------------
  // Fetch V2 pool state
  // -----------------------------------------------------------------------

  useEffect(() => {
    if (!deployment?.v2 || !deployment.tokens) return;
    const v2 = deployment.v2;

    async function fetchV2() {
      const poolsWithAddrs = V2_POOLS.map((pool) => {
        const tokenA = getTokenAddress(pool.tokens[0]);
        const tokenB = getTokenAddress(pool.tokens[1]);
        if (!tokenA || !tokenB) return null;
        const [token0, token1] = sortAddresses(tokenA, tokenB);
        const pairAddress = computePairAddress(
          v2.factory,
          token0,
          token1,
          v2.initCodePairHash,
        );
        return { pool, tokenA, token0, pairAddress };
      }).filter((x): x is NonNullable<typeof x> => x !== null);

      const results = await Promise.allSettled(
        poolsWithAddrs.map(({ pairAddress }) =>
          readContract(pairAddress, V2_PAIR_ABI, "getReserves"),
        ),
      );

      const newStates: Record<string, V2PoolState> = {};
      poolsWithAddrs.forEach(({ pool, tokenA, token0, pairAddress }, i) => {
        const r = results[i];
        if (r?.status !== "fulfilled" || !r.value) return;
        const [reserve0, reserve1] = r.value as [bigint, bigint, number];
        const isTokenAToken0 = tokenA.toLowerCase() === token0.toLowerCase();
        newStates[pool.pair] = {
          reserve0: isTokenAToken0 ? reserve0 : reserve1,
          reserve1: isTokenAToken0 ? reserve1 : reserve0,
          pairAddress,
        };
      });

      setV2States(newStates);
    }

    void fetchV2();
  }, [blockNumber, deployment?.v2.factory]);

  // -----------------------------------------------------------------------
  // Fetch V3 pool state
  // -----------------------------------------------------------------------

  useEffect(() => {
    if (!deployment?.v3 || !deployment.tokens) return;
    const v3 = deployment.v3;

    async function fetchV3() {
      const poolsWithTokens = V3_POOLS.map((pool) => {
        const tokenA = getTokenAddress(pool.tokens[0]);
        const tokenB = getTokenAddress(pool.tokens[1]);
        if (!tokenA || !tokenB) return null;
        return { pool, tokenA, tokenB };
      }).filter((x): x is NonNullable<typeof x> => x !== null);

      // Phase 1: Fetch all pool addresses in parallel
      const addrResults = await Promise.allSettled(
        poolsWithTokens.map(({ tokenA, tokenB, pool }) =>
          readContract(v3.factory, V3_FACTORY_ABI, "getPool", [
            tokenA,
            tokenB,
            pool.feeTier,
          ]),
        ),
      );

      // Phase 2: For valid pools, fetch slot0+liquidity in parallel
      const validPools: {
        pool: (typeof V3_POOLS)[number];
        poolAddress: Address;
      }[] = [];
      addrResults.forEach((r, i) => {
        if (r.status !== "fulfilled" || !r.value) return;
        const entry = poolsWithTokens[i];
        if (!entry) return;
        const addr = r.value as Address;
        if (addr === "0x0000000000000000000000000000000000000000") return;
        validPools.push({
          pool: entry.pool,
          poolAddress: addr,
        });
      });

      const dataResults = await Promise.allSettled(
        validPools.map(async ({ poolAddress }) => {
          const [slot0Result, liqResult] = await Promise.allSettled([
            readContract(poolAddress, V3_POOL_ABI, "slot0"),
            readContract(poolAddress, V3_POOL_ABI, "liquidity"),
          ]);
          return { slot0Result, liqResult };
        }),
      );

      const newStates: Record<string, V3PoolState> = {};
      validPools.forEach(({ pool, poolAddress }, i) => {
        const dataR = dataResults[i];
        if (dataR?.status !== "fulfilled") return;
        const { slot0Result, liqResult } = dataR.value;

        const slot0 =
          slot0Result.status === "fulfilled" ? slot0Result.value : null;
        const liq = liqResult.status === "fulfilled" ? liqResult.value : null;

        if (slot0) {
          const [sqrtPriceX96, tick] = slot0 as [bigint, number, ...unknown[]];
          const liquidity =
            liq !== null && liq !== undefined ? (liq as bigint) : 0n;
          const key = `${pool.pair}-${String(pool.feeTier)}`;
          newStates[key] = { sqrtPriceX96, tick, liquidity, poolAddress };
        }
      });

      setV3States(newStates);
    }

    void fetchV3();
  }, [blockNumber, deployment?.v3.factory]);

  // -----------------------------------------------------------------------
  // Fetch V4 pool state via StateView
  // -----------------------------------------------------------------------

  useEffect(() => {
    if (!deployment?.v4 || !deployment.tokens) return;
    const v4 = deployment.v4;

    async function fetchV4() {
      const ZERO_ADDR = "0x0000000000000000000000000000000000000000" as Address;

      const poolsWithMeta = V4_POOLS.map((pool) => {
        const tokenA = getTokenAddress(pool.tokens[0]);
        const tokenB = getTokenAddress(pool.tokens[1]);
        if (!tokenA || !tokenB) return null;
        const [currency0, currency1] = sortAddresses(tokenA, tokenB);
        const poolId = computePoolId(
          currency0,
          currency1,
          pool.feeTier,
          pool.tickSpacing,
          ZERO_ADDR,
        );
        return { pool, poolId };
      }).filter((x): x is NonNullable<typeof x> => x !== null);

      const results = await Promise.allSettled(
        poolsWithMeta.map(async ({ poolId }) => {
          const [slot0Result, liqResult] = await Promise.allSettled([
            readContract(v4.stateView, STATE_VIEW_ABI, "getSlot0", [poolId]),
            readContract(v4.stateView, STATE_VIEW_ABI, "getLiquidity", [
              poolId,
            ]),
          ]);
          return { slot0Result, liqResult };
        }),
      );

      const newStates: Record<string, V4PoolState> = {};
      poolsWithMeta.forEach(({ pool }, i) => {
        const r = results[i];
        if (r?.status !== "fulfilled") return;
        const { slot0Result, liqResult } = r.value;

        const slot0 =
          slot0Result.status === "fulfilled" ? slot0Result.value : null;
        const liq = liqResult.status === "fulfilled" ? liqResult.value : null;

        if (slot0) {
          const [sqrtPriceX96, tick] = slot0 as [bigint, number, ...unknown[]];
          const liquidity =
            liq !== null && liq !== undefined ? (liq as bigint) : 0n;
          const key = `v4-${pool.pair}`;
          newStates[key] = { sqrtPriceX96, tick, liquidity };
        }
      });

      setV4States(newStates);
    }

    void fetchV4();
  }, [blockNumber, deployment?.v4.stateView]);

  // -----------------------------------------------------------------------
  // Filtered + sorted pools based on search query
  // -----------------------------------------------------------------------

  function applySortV2<T extends { pair: string }>(pools: T[]): T[] {
    if (sortMode === "pair-az")
      return [...pools].sort((a, b) => a.pair.localeCompare(b.pair));
    return pools;
  }

  function applySortV3<T extends { pair: string; feeTier: number }>(
    pools: T[],
  ): T[] {
    if (sortMode === "pair-az")
      return [...pools].sort((a, b) => a.pair.localeCompare(b.pair));
    if (sortMode === "fee-low")
      return [...pools].sort((a, b) => a.feeTier - b.feeTier);
    return pools;
  }

  function applySortV4<T extends { pair: string; tickSpacing: number }>(
    pools: T[],
  ): T[] {
    if (sortMode === "pair-az")
      return [...pools].sort((a, b) => a.pair.localeCompare(b.pair));
    if (sortMode === "fee-low")
      return [...pools].sort((a, b) => a.tickSpacing - b.tickSpacing);
    return pools;
  }

  const filteredV2 = useMemo(() => {
    let pools = V2_POOLS;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      pools = pools.filter((pool) => {
        const pairAddr = v2States[pool.pair]?.pairAddress ?? "";
        return (
          pool.tokens.some((t) => t.toLowerCase().includes(q)) ||
          pool.pair.toLowerCase().includes(q) ||
          pairAddr.toLowerCase().includes(q)
        );
      });
    }
    return applySortV2(pools);
  }, [searchQuery, v2States, sortMode]);

  const filteredV3 = useMemo(() => {
    let pools = V3_POOLS;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      pools = pools.filter((pool) => {
        const poolAddr =
          v3States[`${pool.pair}-${String(pool.feeTier)}`]?.poolAddress ?? "";
        return (
          pool.tokens.some((t) => t.toLowerCase().includes(q)) ||
          pool.pair.toLowerCase().includes(q) ||
          poolAddr.toLowerCase().includes(q)
        );
      });
    }
    return applySortV3(pools);
  }, [searchQuery, v3States, sortMode]);

  const filteredV4 = useMemo(() => {
    let pools = V4_POOLS;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      pools = pools.filter((pool) => {
        return (
          pool.tokens.some((t) => t.toLowerCase().includes(q)) ||
          pool.pair.toLowerCase().includes(q)
        );
      });
    }
    return applySortV4(pools);
  }, [searchQuery, sortMode]);

  // -----------------------------------------------------------------------
  // Summary stats
  // -----------------------------------------------------------------------

  const totalPools = V2_POOLS.length + V3_POOLS.length + V4_POOLS.length;

  const totalV2Tvl = useMemo(() => {
    let sum = 0;
    for (const pool of V2_POOLS) {
      const state = v2States[pool.pair];
      if (!state) continue;
      const d0 = getDecimals(pool.tokens[0]);
      const d1 = getDecimals(pool.tokens[1]);
      sum += computeTvlUsd(
        [
          { symbol: pool.tokens[0], amount: Number(state.reserve0) / 10 ** d0 },
          { symbol: pool.tokens[1], amount: Number(state.reserve1) / 10 ** d1 },
        ],
        DEVENV_TOKEN_PRICES,
      );
    }
    return sum;
  }, [v2States]);

  const activePools = useMemo(() => {
    let count = 0;
    for (const pool of V2_POOLS) {
      const s = v2States[pool.pair];
      if (s && (s.reserve0 > 0n || s.reserve1 > 0n)) count++;
    }
    for (const pool of V3_POOLS) {
      const s = v3States[`${pool.pair}-${String(pool.feeTier)}`];
      if (s && s.liquidity > 0n) count++;
    }
    for (const pool of V4_POOLS) {
      const s = v4States[`v4-${pool.pair}`];
      if (s && s.sqrtPriceX96 !== 0n && s.liquidity > 0n) count++;
    }
    return count;
  }, [v2States, v3States, v4States]);

  const uninitializedV4 = useMemo(() => {
    let count = 0;
    for (const pool of V4_POOLS) {
      const s = v4States[`v4-${pool.pair}`];
      if (s?.sqrtPriceX96 === 0n) count++;
    }
    return count;
  }, [v4States]);

  // -----------------------------------------------------------------------
  // Active tab empty state
  // -----------------------------------------------------------------------

  const activeFilteredCount =
    activeTab === "V2"
      ? filteredV2.length
      : activeTab === "V3"
        ? filteredV3.length
        : filteredV4.length;

  const tabCounts: Record<string, number> = {
    V2: filteredV2.length,
    V3: filteredV3.length,
    V4: filteredV4.length,
  };

  return (
    <div className="pool-explorer">
      {loading && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="card" height="120px" />
          ))}
        </div>
      )}

      {/* Hero stats row */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <PcbCard label="STAT">
          <div className="stat-label mb-1">Total Pools</div>
          <div className="stat-number text-xl">{totalPools}</div>
          <div className="pool-stat-bar mt-3">
            <div
              className="pool-stat-bar-segment pool-stat-bar-v2"
              style={{
                width: `${String((V2_POOLS.length / totalPools) * 100)}%`,
              }}
            />
            <div
              className="pool-stat-bar-segment pool-stat-bar-v3"
              style={{
                width: `${String((V3_POOLS.length / totalPools) * 100)}%`,
              }}
            />
            <div
              className="pool-stat-bar-segment pool-stat-bar-v4"
              style={{
                width: `${String((V4_POOLS.length / totalPools) * 100)}%`,
              }}
            />
          </div>
          <div className="mt-1.5 flex gap-3">
            {PROTOCOL_TABS.map((p) => (
              <span
                key={p.value}
                className="flex items-center gap-1 text-[10px] text-white/40"
              >
                <span
                  className="inline-block h-1.5 w-1.5 rounded-full"
                  style={{ background: p.color }}
                />
                {p.value}
              </span>
            ))}
          </div>
        </PcbCard>

        <PcbCard label="STAT">
          <div className="stat-label mb-1">V2 TVL</div>
          <div className="stat-number text-xl">
            {totalV2Tvl > 0 ? formatUsd(totalV2Tvl) : "\u2014"}
          </div>
          {totalV2Tvl > 0 && (
            <div className="mt-2 font-sans text-[11px] text-[var(--color-success)]">
              Across {V2_POOLS.length} pools
            </div>
          )}
        </PcbCard>

        <PcbCard label="STAT">
          <div className="stat-label mb-1">Active</div>
          <div className="stat-number text-xl">{activePools}</div>
          <div className="pool-active-ring mt-2">
            <svg width="40" height="40" viewBox="0 0 40 40">
              <circle
                cx="20"
                cy="20"
                r="16"
                fill="none"
                stroke="rgba(255,255,255,0.06)"
                strokeWidth="3"
              />
              <circle
                cx="20"
                cy="20"
                r="16"
                fill="none"
                stroke="var(--color-success)"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={`${String((activePools / Math.max(totalPools, 1)) * 100.5)} 100.5`}
                transform="rotate(-90 20 20)"
                style={{
                  transition: "stroke-dasharray 800ms var(--ease-luxury)",
                }}
              />
            </svg>
            <span className="pool-active-ring-label">
              {totalPools > 0
                ? Math.round((activePools / totalPools) * 100)
                : 0}
              %
            </span>
          </div>
        </PcbCard>

        <PcbCard label="STAT">
          <div className="stat-label mb-1">Uninitialized</div>
          <div className="stat-number text-xl">{uninitializedV4}</div>
          {uninitializedV4 > 0 && (
            <div className="mt-2 font-sans text-[11px] text-[var(--color-warning)]">
              V4 pools awaiting liquidity
            </div>
          )}
        </PcbCard>
      </div>

      {/* Search + sort toolbar */}
      <div className="mb-5 flex items-center gap-3">
        <div className="relative flex-1">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/25">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
          </span>
          <input
            type="text"
            placeholder="Filter pools by token or address..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            className="search-input h-10 w-full pl-9 pr-3"
          />
        </div>
        <select
          className="h-10 cursor-pointer border border-[var(--glass-border)] bg-[var(--glass-bg)] px-3 font-mono text-xs text-[var(--color-text-dim)] outline-none transition-colors hover:border-[var(--color-gold-dim)]"
          value={sortMode}
          onChange={(e) => {
            setSortMode(e.target.value as SortMode);
          }}
        >
          <option value="default">Default</option>
          <option value="pair-az">Pair A-Z</option>
          <option value="fee-low">Fee (low to high)</option>
        </select>
      </div>

      {/* Protocol tabs */}
      <div className="pool-tabs mb-5 flex gap-1.5">
        {PROTOCOL_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => {
              setActiveTab(tab.value);
            }}
            className={cn(
              "pool-tab flex items-center gap-2 px-4 py-2.5 font-sans text-sm font-medium transition-all",
              activeTab === tab.value
                ? "pool-tab-active"
                : "text-white/40 hover:bg-white/[0.03] hover:text-white/60",
            )}
            style={
              activeTab === tab.value
                ? ({ "--tab-color": tab.color } as React.CSSProperties)
                : undefined
            }
          >
            <span
              className="inline-block h-2 w-2 rounded-full transition-transform"
              style={{
                background:
                  activeTab === tab.value
                    ? tab.color
                    : "rgba(255,255,255,0.15)",
                transform: activeTab === tab.value ? "scale(1)" : "scale(0.75)",
              }}
            />
            <span>{tab.value}</span>
            <span
              className={cn(
                "rounded-full px-2 py-0.5 font-mono text-[10px] transition-colors",
                activeTab === tab.value
                  ? "bg-white/10 text-white/80"
                  : "bg-white/[0.04] text-white/30",
              )}
            >
              {tabCounts[tab.value]}
            </span>
          </button>
        ))}
      </div>

      <div className="space-y-5">
        {searchQuery && activeFilteredCount === 0 && (
          <EmptyState
            message={`No pools found for '${searchQuery}'. Try a different token or address.`}
          />
        )}

        {activeTab === "V2" && filteredV2.length > 0 && (
          <div>
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <ProtocolBadge protocol="V2" />
                <span className="font-sans text-sm font-medium text-white/70">
                  {filteredV2.length} pools
                </span>
              </div>
              {deployment?.v2 && (
                <span className="text-text-muted flex items-center gap-1 text-xs">
                  Factory:{" "}
                  <AddressDisplay
                    address={deployment.v2.factory}
                    fontSize={10}
                    copyable
                  />
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filteredV2.map((pool) => (
                <V2PoolCard
                  key={pool.pair}
                  pair={pool.pair}
                  tokenAddresses={getTokenPair(pool.tokens)}
                  state={v2States[pool.pair] ?? null}
                  tokenSymbols={pool.tokens}
                />
              ))}
            </div>
          </div>
        )}

        {activeTab === "V3" && filteredV3.length > 0 && (
          <div>
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <ProtocolBadge protocol="V3" />
                <span className="font-sans text-sm font-medium text-white/70">
                  {filteredV3.length} pools
                </span>
              </div>
              {deployment?.v3 && (
                <span className="text-text-muted flex items-center gap-1 text-xs">
                  Factory:{" "}
                  <AddressDisplay
                    address={deployment.v3.factory}
                    fontSize={10}
                    copyable
                  />
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filteredV3.map((pool) => (
                <V3PoolCard
                  key={`${pool.pair}-${String(pool.feeTier)}`}
                  pair={pool.pair}
                  fee={pool.fee}
                  feeTier={pool.feeTier}
                  tokenAddresses={getTokenPair(pool.tokens)}
                  state={
                    v3States[`${pool.pair}-${String(pool.feeTier)}`] ?? null
                  }
                  tokenSymbols={pool.tokens}
                  deployment={deployment ?? null}
                  readContract={readContract}
                />
              ))}
            </div>
          </div>
        )}

        {activeTab === "V4" && filteredV4.length > 0 && (
          <div>
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <ProtocolBadge protocol="V4" />
                <span className="font-sans text-sm font-medium text-white/70">
                  {filteredV4.length} pools
                </span>
              </div>
              {deployment?.v4 && (
                <span className="text-text-muted flex items-center gap-1 text-xs">
                  PoolManager:{" "}
                  <AddressDisplay
                    address={deployment.v4.poolManager}
                    fontSize={10}
                    copyable
                  />
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {filteredV4.map((pool) => (
                <V4PoolCard
                  key={`v4-${pool.pair}`}
                  pair={pool.pair}
                  fee={pool.fee}
                  tickSpacing={pool.tickSpacing}
                  tokenAddresses={getTokenPair(pool.tokens)}
                  state={v4States[`v4-${pool.pair}`] ?? null}
                  tokenSymbols={pool.tokens}
                />
              ))}
            </div>
          </div>
        )}

        {deployment?.tokens && (
          <CollapsibleSection
            title="Token addresses"
            defaultOpen={false}
            badge={String(Object.keys(deployment.tokens).length)}
          >
            <Table
              columns={tokenColumns}
              data={Object.entries(deployment.tokens).map(
                ([symbol, address]) => ({
                  symbol: symbol.toUpperCase(),
                  decimals: getDecimals(symbol),
                  address: String(address),
                }),
              )}
              emptyText="No tokens deployed"
            />
          </CollapsibleSection>
        )}
      </div>
    </div>
  );
}
